import { useReducedMotion } from 'framer-motion';

/**
 * Custom hook for mobile-optimized animations
 * Automatically adjusts animation parameters for better mobile performance
 */
export const useMobileOptimizedAnimation = () => {
  const shouldReduceMotion = useReducedMotion();

  const getOptimizedVariants = (variants = {}) => {
    const defaultVariants = {
      initial: { opacity: 0, y: shouldReduceMotion ? 0 : 20 },
      animate: { opacity: 1, y: 0 },
      exit: { opacity: 0, y: shouldReduceMotion ? 0 : -20 }
    };

    return {
      ...defaultVariants,
      ...variants,
      initial: { ...defaultVariants.initial, ...variants.initial },
      animate: { ...defaultVariants.animate, ...variants.animate },
      exit: { ...defaultVariants.exit, ...variants.exit }
    };
  };

  const getOptimizedTransition = (customTransition = {}) => {
    const baseTransition = {
      duration: shouldReduceMotion ? 0.2 : 0.5,
      ease: 'easeOut',
      type: 'tween', // Better performance than spring on mobile
      // Prevent flickering
      willChange: 'transform, opacity',
      // Add frame rate optimization
      stiffness: 100,
      damping: 20
    };

    return {
      ...baseTransition,
      ...customTransition
    };
  };

  const getStaggeredTransition = (index, customTransition = {}) => {
    const staggerDelay = shouldReduceMotion ? 0 : index * 0.1;
    
    return {
      ...getOptimizedTransition(customTransition),
      delay: staggerDelay
    };
  };

  const getInViewVariants = () => ({
    initial: { opacity: 0, y: shouldReduceMotion ? 0 : 30 },
    animate: { opacity: 1, y: 0 },
    transition: getOptimizedTransition()
  });

  const getHoverVariants = () => ({
    whileHover: shouldReduceMotion ? {} : { 
      scale: 1.02,
      transition: { 
        duration: 0.2, 
        ease: 'easeOut',
        type: 'tween' // Prevent spring flickering
      }
    },
    whileTap: shouldReduceMotion ? {} : { 
      scale: 0.98,
      transition: { 
        duration: 0.1, 
        ease: 'easeOut',
        type: 'tween' // Prevent spring flickering
      }
    }
  });

  return {
    shouldReduceMotion,
    getOptimizedVariants,
    getOptimizedTransition,
    getStaggeredTransition,
    getInViewVariants,
    getHoverVariants
  };
};

export default useMobileOptimizedAnimation;
